import { z } from 'zod';

import { IsoDateTimeSchema, NullableIsoDateTimeSchema } from '../common/datetime';
import { CountryCodeSchema, TimezoneSchema } from '../common/fields';
import { CompanyId } from '../common/ids';
import { CursorQuerySchema, pageSchema } from '../common/pagination';
import { TimesheetLayoutSchema } from '../common/schedules';

import { AdminUserSchema } from './admins';

// ---------------------------------------------------------------------------
// Enums (data-model.md · companies)
// ---------------------------------------------------------------------------

export const CompanyStatus = z.enum(['ACTIVE', 'SUSPENDED']);
export type CompanyStatus = z.infer<typeof CompanyStatus>;

export const MarkerShowsName = z.enum(['FIRST_NAME', 'INITIALS', 'NONE']);
export type MarkerShowsName = z.infer<typeof MarkerShowsName>;

export const LocationPolicy = z.enum(['SIGNAL', 'IGNORE']);
export type LocationPolicy = z.infer<typeof LocationPolicy>;

// ---------------------------------------------------------------------------
// Campos base
// ---------------------------------------------------------------------------

const companyCoreFields = {
  legal_name: z.string().min(2).max(200),
  trade_name: z.string().min(1).max(120),
  tax_id: z.string().min(5).max(32),
  country: CountryCodeSchema,
  default_timezone: TimezoneSchema,
};

/**
 * Configuración operativa por empresa con los rangos de data-model.md.
 * Los valores por defecto son los de la tabla `companies`.
 */
export const CompanySettingsSchema = z.object({
  max_open_shift_hours: z.number().int().min(4).max(48),
  marker_shows_name: MarkerShowsName,
  location_policy: LocationPolicy,
  // 005 · rangos de retención (research R-23, FR-019): 5–10 años, 30–365 días, 6–60 meses.
  // Los defaults (5 / 90 / 24) no cambian.
  attendance_retention_years: z.number().int().min(5).max(10),
  location_retention_days: z.number().int().min(30).max(365),
  anonymize_after_months: z.number().int().min(6).max(60),
  autonomous_mode_enabled: z.boolean(),
  /**
   * Rango del `CHECK companies_autonomous_mode_after_seconds_check` (`0000_init.sql`).
   *
   * **004 · T086 — el valor efectivo puede ser mayor que el configurado.** El marcador nunca entra
   * en modo autónomo antes de que el servidor pueda declarar el terminal `OFFLINE`, o cada
   * verificación autónoma de esa franja moriría con `AUTONOMOUS_DISABLED` (FR-045, A10). El suelo
   * es `TERMINAL_OFFLINE_AFTER_SECONDS + TERMINAL_HEARTBEAT_SECONDS` (60 s con los defectos de
   * producción), así que el defecto de 20 s de aquí **no se aplica tal cual**. La regla vive en
   * `apps/api/src/modules/vpp-config/service.ts` (`effectiveAutonomousAfterSeconds`) y la aplica el
   * marcador en `apps/marker/src/features/display/marker-state.ts` (`autonomousEntryAtMs`); no se
   * puede validar aquí porque cruza una columna de empresa con dos variables de entorno.
   */
  autonomous_mode_after_seconds: z.number().int().min(10).max(120),
  autonomous_marks_per_worker_interval_s: z.number().int().min(30).max(600),
  // --- 004 · parámetros VPP de nivel empresa (data-model.md · "Columnas nuevas en companies") ---
  // Los rangos son, letra por letra, los CHECK de `0003_attendance.sql:149-152`. Los de nivel
  // global viven en `config/env.ts`; `modules/vpp-config` compone ambos (research R-16, FR-037).
  /** `companies_vpp_queue_max_size_check`: tamaño máximo de la cola por terminal (FR-013). */
  vpp_queue_max_size: z.number().int().min(1).max(10),
  /** `companies_vpp_queue_max_wait_ms_check`: espera máxima en cola (FR-014). */
  vpp_queue_max_wait_ms: z.number().int().min(5000).max(30000),
  /** `companies_vpp_marker_confirmation_check` (primera mitad): confirmación en pantalla (FR-033). */
  vpp_marker_confirmation_ms: z.number().int().min(500).max(2000),
  /** `companies_vpp_marker_confirmation_check` (segunda mitad): confirmación con cola (FR-033). */
  vpp_marker_confirmation_busy_ms: z.number().int().min(300).max(1500),
  /** `companies_geo_low_accuracy_threshold_check`: umbral de `LOW_ACCURACY` en metros (FR-029). */
  geo_low_accuracy_threshold_m: z.number().int().min(30).max(500),
  /**
   * 007 · `companies_regularization_window_days_check` (0008): días operativos hacia atrás, contando
   * hoy, en los que Staff detecta huecos y deja proponer la hora faltante. No afecta a las pedidas
   * por el admin (no vencen).
   */
  regularization_window_days: z.number().int().min(1).max(30),
  // --- 008 · horarios y tareo (data-model.md · columnas nuevas de `companies`, research R-6/R-7) ---
  // Se editan por `PATCH /company/settings` con la misma auditoría `COMPANY_UPDATED` (T026).
  /** Minutos de gracia de la entrada antes de contar tardanza (la plantilla puede sobrescribirlo). */
  late_tolerance_minutes: z.number().int().min(0).max(60),
  /** Minutos de gracia de la salida antes de contar salida anticipada. */
  early_leave_tolerance_minutes: z.number().int().min(0).max(60),
  /** Ventana alrededor de cada turno en la que una marca se atribuye a ese turno. */
  shift_match_window_minutes: z.number().int().min(60).max(480),
  /** Días hacia adelante que el proceso diario genera desde los patrones activos. */
  schedule_horizon_days: z.number().int().min(14).max(120),
  /** Formato del Excel del tareo (sin `CHECK` en la base; catálogo en contratos). */
  timesheet_layout: TimesheetLayoutSchema,
});
export type CompanySettings = z.infer<typeof CompanySettingsSchema>;

/** Defaults de `companies` (data-model.md). Útil para seeds y formularios. */
export const COMPANY_SETTINGS_DEFAULTS: CompanySettings = Object.freeze({
  max_open_shift_hours: 16,
  marker_shows_name: 'FIRST_NAME',
  location_policy: 'SIGNAL',
  attendance_retention_years: 5,
  location_retention_days: 90,
  anonymize_after_months: 24,
  autonomous_mode_enabled: true,
  autonomous_mode_after_seconds: 20,
  autonomous_marks_per_worker_interval_s: 60,
  // 004: mismos DEFAULT que `0003_attendance.sql:144-148`.
  vpp_queue_max_size: 5,
  vpp_queue_max_wait_ms: 12000,
  vpp_marker_confirmation_ms: 1000,
  vpp_marker_confirmation_busy_ms: 700,
  geo_low_accuracy_threshold_m: 100,
  // 007: mismo DEFAULT que `0008_mark_regularizations.sql`.
  regularization_window_days: 7,
  // 008: mismos DEFAULT que `0009_schedules_timesheet.sql`.
  late_tolerance_minutes: 5,
  early_leave_tolerance_minutes: 0,
  shift_match_window_minutes: 240,
  schedule_horizon_days: 56,
  timesheet_layout: 'STANDARD',
});

// ---------------------------------------------------------------------------
// Entidad
// ---------------------------------------------------------------------------

export const CompanySchema = z.object({
  id: CompanyId,
  ...companyCoreFields,
  status: CompanyStatus,
  suspended_at: NullableIsoDateTimeSchema,
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
  settings: CompanySettingsSchema,
});
export type Company = z.infer<typeof CompanySchema>;

/**
 * Valores **derivados** de la configuración: lo que de verdad rige, calculado por el servidor,
 * de solo lectura y sin equivalente en ninguna columna (004 · T077/T086).
 *
 * `autonomous_mode_after_seconds` es el resultado de
 * `effectiveAutonomousAfterSeconds` (`apps/api/src/modules/vpp-config/service.ts`): el mayor entre
 * lo que configuró la empresa y el suelo de la instalación
 * (`TERMINAL_OFFLINE_AFTER_SECONDS + TERMINAL_HEARTBEAT_SECONDS`, 60 s con los valores de
 * producción). Guardar 20 s es legal —es el propio defecto de la columna— y simplemente no tiene
 * efecto: el marcador no puede entrar en modo autónomo antes de que el servidor pueda declararlo
 * sin conexión, o toda verificación de esa franja moriría con `AUTONOMOUS_DISABLED` (FR-045, A10).
 *
 * El Admin **muestra** este número junto al campo editable; no rechaza el guardado. Las dos
 * variables de entorno que componen el suelo **no viajan** al navegador: solo su resultado.
 */
export const CompanyEffectiveSettingsSchema = z.object({
  autonomous_mode_after_seconds: z.number().int().positive(),
  /**
   * 005 · hora local (0–23, en la zona de la empresa) a la que corren la purga de ubicaciones y la
   * anonimización: `RETENTION_LOCAL_HOUR` de la instalación. La sección "Retención y privacidad"
   * del Admin la muestra en vez de suponer la de fábrica.
   */
  retention_local_hour: z.number().int().min(0).max(23),
});
export type CompanyEffectiveSettings = z.infer<typeof CompanyEffectiveSettingsSchema>;

/**
 * `GET /company`: la empresa de la sesión. Para `SITE_MANAGER`/`VIEWER` la API omite `settings`
 * (contracts/admin-api.md · "Empresa actual"), y con ellos `settings_effective`: quien no puede
 * ver la configuración tampoco necesita saber qué valor rige.
 */
export const CurrentCompanyResponseSchema = CompanySchema.extend({
  settings: CompanySettingsSchema.optional(),
  /** Presente exactamente cuando viaja `settings`. */
  settings_effective: CompanyEffectiveSettingsSchema.optional(),
});
export type CurrentCompanyResponse = z.infer<typeof CurrentCompanyResponseSchema>;

/** Elemento de `GET /companies`: `Company & { admins_count, sites_count }`. */
export const CompanyListItemSchema = CompanySchema.extend({
  admins_count: z.number().int().nonnegative(),
  sites_count: z.number().int().nonnegative(),
});
export type CompanyListItem = z.infer<typeof CompanyListItemSchema>;

/**
 * DTO interno para los realms `staff` y `terminal` (data-model.md · "Configuración por empresa
 * expuesta a otras superficies"). No se expone en el realm admin.
 */
export const CompanyStatusViewSchema = z.object({
  id: CompanyId,
  trade_name: z.string(),
  status: CompanyStatus,
  default_timezone: TimezoneSchema,
});
export type CompanyStatusView = z.infer<typeof CompanyStatusViewSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** `GET /companies?status&q&cursor&limit` */
export const CompanyListQuerySchema = CursorQuerySchema.extend({
  status: CompanyStatus.optional(),
  q: z.string().min(1).max(120).optional(),
});
export type CompanyListQuery = z.infer<typeof CompanyListQuerySchema>;

/** `POST /companies` */
export const CreateCompanyBodySchema = z.object({
  ...companyCoreFields,
  first_admin: z.object({
    name: z.string().min(1).max(120),
    email: z.email().max(254),
  }),
});
export type CreateCompanyBody = z.infer<typeof CreateCompanyBodySchema>;

/** `PATCH /companies/{id}`: parcial de los datos de identificación. */
export const UpdateCompanyBodySchema = z.object(companyCoreFields).partial();
export type UpdateCompanyBody = z.infer<typeof UpdateCompanyBodySchema>;

/** `PATCH /company/settings`: parcial de `CompanySettings`. */
export const UpdateCompanySettingsBodySchema = CompanySettingsSchema.partial();
export type UpdateCompanySettingsBody = z.infer<typeof UpdateCompanySettingsBodySchema>;

/** `POST /companies/{id}/suspend` */
export const SuspendCompanyBodySchema = z.object({
  reason: z.string().min(1).max(500),
});
export type SuspendCompanyBody = z.infer<typeof SuspendCompanyBodySchema>;

/** Parámetro de ruta `{id}` de empresa. */
export const CompanyIdParamsSchema = z.object({ id: CompanyId });
export type CompanyIdParams = z.infer<typeof CompanyIdParamsSchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

/** `200 Page<Company & { admins_count, sites_count }>` */
export const CompanyListResponseSchema = pageSchema(CompanyListItemSchema);
export type CompanyListResponse = z.infer<typeof CompanyListResponseSchema>;

/** `201` de `POST /companies`. `setup_link` solo se muestra una vez y nunca se loguea. */
export const CreateCompanyResponseSchema = z.object({
  company: CompanySchema,
  first_admin: AdminUserSchema,
  setup_link: z.url(),
  setup_expires_at: IsoDateTimeSchema,
});
export type CreateCompanyResponse = z.infer<typeof CreateCompanyResponseSchema>;

/** `200` de `GET /companies/{id}` */
export const CompanyDetailResponseSchema = z.object({
  company: CompanySchema,
  admins: z.array(AdminUserSchema),
});
export type CompanyDetailResponse = z.infer<typeof CompanyDetailResponseSchema>;

/** `200` de `POST /companies/{id}/support-session` (nueva sesión acotada en modo soporte). */
export const SupportSessionResponseSchema = z.object({
  admin: AdminUserSchema,
  company: CompanySchema,
  support_mode: z.literal(true),
});
export type SupportSessionResponse = z.infer<typeof SupportSessionResponseSchema>;
